import CloseIcon from '@mui/icons-material/Close';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import dabaduIcon from 'assets/images/trade-in/dabadu-icon.png';
import { CREDIT_SCORE_V2_MODAL_CLOSE, LOCAL_STORAGE_KEYS } from 'constants/CommonConstants';
import useQueryParams from 'hooks/UseQueryParams';
import i18n from 'i18next';
import React, { useEffect, useState } from 'react';
import TagManager from 'react-gtm-module';
import { FieldValues, SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Lottie from 'react-lottie-player';
import { useDispatch, useSelector } from 'react-redux';
import DynamicStepperForm from 'sharedComponents/dynamicStepperForm';
import { StepConfigItem } from 'sharedComponents/dynamicStepperForm/dynamicStepperFormInterface';
import { DefaultModal } from 'sharedComponents/modal';
import { clearLeadCreation, employeeHistory } from 'store/CreditScoreState';
import { RootState } from 'store/store';
import {
  fsBottomCenterShape,
  fsLeftBLShape,
  fsLeftTLShape,
  fsLeftTRShape,
  logoSvg,
  mainImg,
} from '../../assets/images';
import { MODULE_NAME } from './CreditConstants';
import { EmploymentDetails } from './CreditScoreInterface';
import { creditScoreStepConfig1 } from './StepConfig';
import './credit-report.scss';
import celebrationMain from './json-animation/celebration-main.json';
import celebration from './json-animation/celebration.json';
import Logger from 'utils/logger';

const CreditScore: React.FC = () => {
  const { t } = useTranslation();

  const hasCalledMounted = React.useRef(false);

  const queryParams = useQueryParams();

  const type = queryParams.get('type');
  const version = queryParams.get('version');

  const [isSuccessModal, setIsSuccessModal] = useState<boolean>(false);
  const [initialStep, setInitialStep] = React.useState(0);

  const dispatch: ThunkDispatch<RootState, AnyAction, AnyAction> = useDispatch();

  const { authenticateClientData } = useSelector((state: RootState) => state.auth) || {};

  const {
    publicLeadCreationData: {
      data: { Lead: { _id, leadId } = { _id: '', leadId: '' } } = { Lead: { _id: '', leadId: '' } },
    },
  } = useSelector((state: RootState) => state.creditScoreStore);

  const widgetData = authenticateClientData?.data?.widgetData || {};
  const gtmID = widgetData?.gtmID || '';

  let className;

  if (type === 'button' || type === 'banner') {
    className = 'csc-screens csc-screens--modal';
  } else if (type === 'embed') {
    className = 'csc-screens csc-screens--embedded';
  } else {
    className = 'csc-screens csc-screens--full-page';
  }

  useEffect(() => {
    i18n.changeLanguage(authenticateClientData?.data?.widgetData?.language || 'en');

    if (gtmID) {
      // GTM initialize on component mount
      const tagManagerArgs = {
        gtmId: gtmID,
      };

      TagManager.initialize(tagManagerArgs);
    }
  }, [authenticateClientData, gtmID]);

  useEffect(() => {
    if (hasCalledMounted.current) return;
    hasCalledMounted.current = true;

    if (window.self !== window.top) {
      document.body.classList.add('credit-modal-open');
    }
  }, []);

  const clearLocalStorage = () => {
    localStorage.setItem(`${MODULE_NAME}-currentStep`, '0');
    localStorage.removeItem(MODULE_NAME);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.draftLeadId);
    dispatch(clearLeadCreation());
    setIsSuccessModal(true);
    setInitialStep(1);
  };

  const handleFinalSubmit: SubmitHandler<FieldValues> = async (formData) => {
    const {
      grossMonth,
      timeAtJobYear,
      timeAtJobMonth,
      currentEmployerName,
      jobTitle,
      recidenceYear,
      recidenceMonth,
      rentOrOwn,
      monthlyHousePayment,
    } = formData;

    const updatedData: EmploymentDetails = {
      address: {
        yearsAtResidence: recidenceYear,
        monthsAtResidence: recidenceMonth,
      },
      employeeHistory: {
        yearsAtJob: timeAtJobYear,
        monthsAtJob: timeAtJobMonth,
        employer: currentEmployerName,
        income: grossMonth,
        occupation: jobTitle,
      },
      homeMortgage: {
        residenceType: rentOrOwn,
        monthlyMortgageOrRent: monthlyHousePayment,
      },
      leadId: _id,
    };

    dispatch(employeeHistory(updatedData)).then((res) => {
      Logger.custom('employment_details_submitted', {
        id: leadId || '',
        client: authenticateClientData?.data?.client || '',
        widgetName: authenticateClientData?.data?.widgetName || '',
        module: 'Credit Score',
      });

      if (Object.keys(res?.payload).length && res?.payload?.message) {
        clearLocalStorage();
      }
    });
  };

  const onCloseModal = () => {
    window.parent.postMessage(
      { type: CREDIT_SCORE_V2_MODAL_CLOSE, data: { close: true }, res: { module: 'credit-score' } },
      '*'
    );
    dispatch(clearLeadCreation());
    localStorage.setItem(`${MODULE_NAME}-currentStep`, '0');
    localStorage.removeItem(MODULE_NAME);
  };

  const stepConfigsByTypeAndVersion: {
    [type: string]: { [version: string]: StepConfigItem[] };
  } = {
    banner: {
      '1.0': creditScoreStepConfig1(t),
    },
    button: {
      '1.0': creditScoreStepConfig1(t),
    },
    embed: {
      '1.0': creditScoreStepConfig1(t),
    },
  };

  const getStepConfig = (type: string | null, version: string | null): StepConfigItem[] => {
    if (!type || !version) {
      console.error('Invalid type or version');
      return [];
    }

    const typeConfig = stepConfigsByTypeAndVersion[type];
    if (!typeConfig) {
      console.error('Unknown type:', type);
      return [];
    }

    const config = typeConfig[version];
    if (!config) {
      console.error(`No configuration found for type "${type}" and version "${version}"`);
      return [];
    }

    return config;
  };

  const stepConfig = getStepConfig(type, version);

  return (
    <>
      <div className={className}>
        <div className='csc-screens__shape csc-screens__shape--bottom-center'>
          <img src={fsBottomCenterShape} alt='Dabadu' className='img-fluid' />
        </div>
        <div className='csc-screens__col csc-screens__col--left'>
          <div className='csc-screens__left'>
            <div className='csc-screens__logo'>
              <img src={logoSvg} alt='Dabadu' className='img-fluid' />
            </div>
            <div className='csc-screens__shape csc-screens__shape--ltl'>
              <img src={fsLeftTLShape} alt='Dabadu' className='img-fluid' />
            </div>
            <div className='csc-screens__shape csc-screens__shape--ltr'>
              <img src={fsLeftTRShape} alt='Dabadu' className='img-fluid' />
            </div>
            <div className='csc-screens__shape csc-screens__shape--lbl'>
              <img src={fsLeftBLShape} alt='Dabadu' className='img-fluid' />
            </div>
            <div className='csc-screens__main-img'>
              <img src={mainImg} alt='Dabadu' className='img-fluid' />
            </div>
          </div>
        </div>

        <div className='csc-screens__col csc-screens__col--right'>
          {window.self !== window.top && (
            <header className='csc-screens__col__header'>
              <img src={dabaduIcon} alt='Dabadu' className='img-fluid' />

              <span className='close' onClick={onCloseModal}>
                <CloseIcon />
              </span>
            </header>
          )}
          {version != null ? (
            <DynamicStepperForm
              stepConfig={stepConfig}
              initialStep={initialStep}
              onFinish={handleFinalSubmit}
              moduleConfig={{
                name: MODULE_NAME,
              }}
            />
          ) : null}
        </div>
      </div>

      <DefaultModal
        open={isSuccessModal}
        handleClose={() => {
          setIsSuccessModal(false);
          onCloseModal();
        }}
        customClass='csc-thankyou-modal'
        childern={
          <div className='modal-body'>
            <p className='csc-thankyou-modal__title fw-600'>{t('leadGen.successMsg')}</p>
            <div className='csc-thankyou-modal__lottie-animation'>
              <div className='csc-thankyou-modal__lottie-animation__left'>
                <Lottie loop animationData={celebration} play className='' />
              </div>
              <div className='csc-thankyou-modal__lottie-animation__middle'>
                <Lottie loop animationData={celebrationMain} play className='' />
              </div>
              <div className='csc-thankyou-modal__lottie-animation__right'>
                <Lottie loop animationData={celebration} play className='' />
              </div>
            </div>
          </div>
        }
      />
    </>
  );
};

export default CreditScore;
// export default withLoading(CreditScore);
